import { Controller } from "@hotwired/stimulus"

/**
 * Off-canvas mobile navigation drawer for < lg viewports.
 *
 * A bottom-anchored "Menu" pill opens a left slide-in drawer that reuses the
 * layout's sidebar nav items. The controller lives on the layout's outer
 * element so the trigger pill and the drawer resolve to one instance.
 *
 * Independent of the desktop `sidebar-collapse` controller, which only ever
 * touches the `hidden lg:flex` <aside>; the two never share a DOM node.
 */
export default class extends Controller {
  static targets = ["panel", "backdrop", "trigger"]
  static values = { open: { type: Boolean, default: false } }

  open(event) {
    if (event) event.preventDefault()
    this.openValue = true
  }

  close() {
    this.openValue = false
  }

  toggle() {
    this.openValue = !this.openValue
  }

  closeOnNavigate(event) {
    if (event.target.closest("a")) this.close()
  }

  closeOnEscape(event) {
    if (event.key === "Escape" && this.openValue) this.close()
  }

  closeOnDesktop() {
    if (this.openValue && window.matchMedia("(min-width: 1024px)").matches) this.close()
  }

  // Force-close (no animation concerns) before a page is cached by Turbo so a
  // restored snapshot is never stuck open / scroll-locked.
  reset() {
    this.openValue = false
  }

  openValueChanged() {
    if (this.hasPanelTarget) {
      this.panelTarget.classList.toggle("-translate-x-full", !this.openValue)
      this.panelTarget.classList.toggle("translate-x-0", this.openValue)
      this.panelTarget.setAttribute("aria-hidden", this.openValue ? "false" : "true")
    }

    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.toggle("opacity-0", !this.openValue)
      this.backdropTarget.classList.toggle("pointer-events-none", !this.openValue)
    }

    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", this.openValue ? "true" : "false")
    }

    document.documentElement.classList.toggle("overflow-hidden", this.openValue)
  }

  disconnect() {
    // Never leave the page scroll-locked if the controller is torn down.
    document.documentElement.classList.remove("overflow-hidden")
  }
}
